/**
 * Write-review form web component.
 *
 * Reads ?o= and ?p= from URL (set by the Klaviyo review-request email link).
 * Submits to /v1/me/reviews via reviews-client. Customer must be logged in
 * (Shopify routes through /account/login if not).
 *
 * On success: hides form, shows thank-you message + points awarded.
 */

import { ReviewsClient, describeReviewError } from './reviews-client-v2.js';

class WriteReviewForm extends HTMLElement {
  constructor() {
    super();
    this.client = new ReviewsClient();
  }

  connectedCallback() {
    if (this.dataset.mounted === '1') return;
    this.dataset.mounted = '1';

    this.refs = {
      form: this.querySelector('form'),
      productId: this.querySelector('[name="product_id"]'),
      orderId: this.querySelector('[name="order_id"]'),
      ratingInputs: this.querySelectorAll('[name="rating"]'),
      title: this.querySelector('[name="title"]'),
      body: this.querySelector('[name="body"]'),
      customerName: this.querySelector('[name="customer_name"]'),
      submit: this.querySelector('[type="submit"]'),
      error: this.querySelector('[data-error]'),
      success: this.querySelector('[data-success]'),
      points: this.querySelector('[data-points]'),
      formWrap: this.querySelector('[data-form-wrap]'),
      productLabel: this.querySelector('[data-product-label]'),
    };

    // Pull product + order ids from URL params if not already pre-filled
    const params = new URLSearchParams(window.location.search);
    const p = params.get('p');
    const o = params.get('o');
    if (p && this.refs.productId && !this.refs.productId.value) {
      this.refs.productId.value = p;
    }
    if (o && this.refs.orderId && !this.refs.orderId.value) {
      this.refs.orderId.value = o;
    }

    if (this.refs.form) {
      this.refs.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    // Show "you can't review without an order ref" notice if URL params missing
    if (!this.refs.productId?.value || !this.refs.orderId?.value) {
      this.showError({ code: 'order_not_found' });
      if (this.refs.submit) this.refs.submit.disabled = true;
    }
  }

  showError(err) {
    if (!this.refs.error) return;
    this.refs.error.hidden = false;
    this.refs.error.textContent = describeReviewError(err);
  }

  hideError() {
    if (this.refs.error) {
      this.refs.error.hidden = true;
      this.refs.error.textContent = '';
    }
  }

  async onSubmit(e) {
    e.preventDefault();
    this.hideError();

    if (!this.refs.productId?.value || !this.refs.orderId?.value) {
      this.showError({ code: 'order_not_found' });
      return;
    }

    const rating = this.selectedRating();
    if (!rating) {
      this.showError({ code: 'rating_invalid' });
      return;
    }

    const body = (this.refs.body?.value ?? '').trim();
    if (body.length < 10) {
      this.showError({ code: 'body_too_short' });
      return;
    }

    if (this.refs.submit) this.refs.submit.disabled = true;

    try {
      const result = await this.client.submit({
        product_id: this.refs.productId.value,
        order_id: this.refs.orderId.value,
        rating,
        title: this.refs.title?.value?.trim() || null,
        body,
        customer_name: (this.refs.customerName?.value ?? '').trim() || 'Anonymous',
      });

      if (this.refs.formWrap) this.refs.formWrap.hidden = true;
      if (this.refs.success) {
        this.refs.success.hidden = false;
        if (this.refs.points && result.points_awarded) {
          this.refs.points.textContent = String(result.points_awarded);
        }
      }
    } catch (err) {
      this.showError(err);
      if (this.refs.submit) this.refs.submit.disabled = false;
    }
  }

  selectedRating() {
    for (const input of this.refs.ratingInputs ?? []) {
      if (input.checked) {
        const val = parseInt(input.value, 10);
        if (val >= 1 && val <= 5) return val;
      }
    }
    return null;
  }
}

if (!customElements.get('write-review-form')) {
  customElements.define('write-review-form', WriteReviewForm);
}
